/**
 * Centralized error parsing utility
 * Safely converts API error responses into user-friendly strings
 */

/**
 * Parse API error response and return a user-friendly error message
 * Handles:
 * - String errors: returned as-is
 * - Pydantic validation errors: array of {type, loc, msg, input, url} objects
 * - Object errors with message/msg property
 * - Unknown formats: returns default message
 * 
 * @param {Error|Object} error - Axios error object or any error
 * @param {string} defaultMsg - Fallback message if parsing fails
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error, defaultMsg = 'Operation failed') {
  // Handle null/undefined
  if (!error) return defaultMsg;

  // Extract detail from Axios error response
  const detail = error?.response?.data?.detail ?? error?.detail ?? error;

  // If detail is null/undefined, return default
  if (detail === null || detail === undefined) return defaultMsg;

  // If detail is already a string, return it directly
  if (typeof detail === 'string') return detail;

  // If detail is an array (Pydantic validation errors)
  if (Array.isArray(detail)) {
    const messages = detail
      .map(err => {
        // Pydantic v2 format: {type, loc, msg, input, url}
        if (err.msg) return err.msg;
        // Alternative format
        if (err.message) return err.message;
        // Last resort: stringify
        return JSON.stringify(err);
      })
      .filter(Boolean);
    
    return messages.length > 0 ? messages.join('; ') : defaultMsg;
  }

  // If detail is an object with msg or message property
  if (typeof detail === 'object') {
    if (detail.msg) return String(detail.msg);
    if (detail.message) return String(detail.message);
    if (detail.error) return String(detail.error);
    
    // Special handling for payment required errors
    if (detail.requires_payment && detail.balance_due !== undefined) {
      return `Payment required! Balance due: ₹${detail.balance_due?.toFixed(0)}`;
    }
    
    // Last resort for objects: try to create meaningful message
    try {
      const str = JSON.stringify(detail);
      // If it's too long or looks like internal data, use default
      if (str.length > 200 || str.includes('input')) {
        return defaultMsg;
      }
      return str;
    } catch {
      return defaultMsg;
    }
  }

  // For numbers, booleans, etc.
  return String(detail) || defaultMsg;
}

/**
 * Quick helper for toast error display
 * @param {Error} error - Axios error object
 * @param {string} defaultMsg - Fallback message
 * @returns {string} - Error message suitable for toast display
 */
export function toastError(error, defaultMsg = 'Operation failed') {
  return getErrorMessage(error, defaultMsg);
}

export default getErrorMessage;
